// Flujo de diligenciamiento de una inspección (solo admin/auxiliar):
// 1) elegir empresa (solo las que tienen esta inspección asignada)
// 2) capturar la ubicación actual (bloquea si falla)
// 3) responder las preguntas y enviar de forma definitiva
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { X, Loader, AlertCircle, CheckCircle, MapPin, ClipboardCheck } from "lucide-react";
import { api } from "../api";
import { calculateDateValue, calculationLabel, isFutureDate, todayISO } from "../utils/dateCalculations";

const COMPLIANCE_OPTIONS = [
  { value: "cumple", label: "Cumple", color: "#00A896" },
  { value: "no_cumple", label: "No cumple", color: "#C53030" },
  { value: "cumple_parcial", label: "Cumple parcialmente", color: "#B45309" },
  { value: "no_aplica", label: "No aplica", color: "#64748B" },
];
const NEEDS_FOLLOW_UP = ["no_cumple", "cumple_parcial"];

function ComplianceQuestion({ field, value, onChange, followUpValues, onFollowUpChange, onFollowUpFile }) {
  const showFollowUps = NEEDS_FOLLOW_UP.includes(value) && (field.followUps || []).length > 0;
  return (
    <div className="form-row">
      <label>{field.label}{field.required ? " *" : ""}</label>
      {field.description && (
        <div style={{ marginTop: -8, marginBottom: 8, fontSize: "0.8rem", color: "var(--text-muted)" }}>{field.description}</div>
      )}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {COMPLIANCE_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            style={{
              padding: "8px 14px", borderRadius: 20, fontSize: "0.8rem", fontWeight: 600, cursor: "pointer",
              border: `1px solid ${value === option.value ? option.color : "var(--border)"}`,
              background: value === option.value ? option.color : "transparent",
              color: value === option.value ? "#fff" : "var(--text-muted)",
            }}
          >
            {option.label}
          </button>
        ))}
      </div>

      {showFollowUps && (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 10, padding: 10, background: "var(--bg)", borderRadius: 8, border: "1px solid var(--border)" }}>
          {field.followUps.map((followUp) => (
            <div key={followUp.id}>
              <label style={{ fontSize: "0.8rem" }}>{followUp.label}{followUp.required ? " *" : ""}</label>
              {followUp.type === "file" ? (
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(event) => onFollowUpFile(field.id, followUp.id, event.target.files[0] || null)}
                />
              ) : followUp.type === "textarea" ? (
                <textarea
                  className="form-input"
                  rows={3}
                  value={followUpValues[followUp.id] || ""}
                  onChange={(event) => onFollowUpChange(field.id, followUp.id, event.target.value)}
                />
              ) : (
                <input
                  className="form-input"
                  type={followUp.type === "number" ? "number" : "text"}
                  value={followUpValues[followUp.id] || ""}
                  onChange={(event) => onFollowUpChange(field.id, followUp.id, event.target.value)}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function GenericQuestion({ field, value, onChange, onFile }) {
  return (
    <div className="form-row">
      <label>{field.label}{field.required ? " *" : ""}</label>
      {field.description && (
        <div style={{ marginTop: -8, marginBottom: 8, fontSize: "0.8rem", color: "var(--text-muted)" }}>{field.description}</div>
      )}
      {field.type === "textarea" ? (
        <textarea className="form-input" rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} />
      ) : field.type === "number" ? (
        <input className="form-input" type="number" value={value || ""} onChange={(e) => onChange(e.target.value)} />
      ) : field.type === "date" ? (
        <>
          <input
            className="form-input"
            type="date"
            max={field.dateCalculation ? todayISO() : undefined}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            style={field.dateCalculation && isFutureDate(value) ? { borderColor: "#DC2626" } : undefined}
          />
          {field.dateCalculation && isFutureDate(value) && (
            <div style={{ marginTop: 6, fontSize: "0.78rem", color: "#DC2626" }}>
              La fecha no puede ser posterior a hoy.
            </div>
          )}
          {field.dateCalculation && (
            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 4 }}>
              <label style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>
                {calculationLabel(field.dateCalculation)}
              </label>
              <input
                className="form-input"
                readOnly
                tabIndex={-1}
                value={isFutureDate(value) ? "" : calculateDateValue(value, field.dateCalculation)}
                placeholder="Se calcula al elegir la fecha"
                style={{ background: "var(--bg)", color: "var(--primary)", fontWeight: 600, cursor: "default" }}
              />
            </div>
          )}
        </>
      ) : field.type === "select" ? (
        <select className="form-input" value={value || ""} onChange={(e) => onChange(e.target.value)}>
          <option value="" disabled>Selecciona una opcion</option>
          {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : field.type === "boolean" ? (
        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} /> Si
        </label>
      ) : field.type === "file" ? (
        <input type="file" accept="image/*,.pdf" onChange={(e) => onFile(e.target.files[0] || null)} />
      ) : (
        <input className="form-input" type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

export default function InspectionFillModal({ inspection, companies, onClose, onSubmitted, publicToken = null }) {
  const [step, setStep] = useState(publicToken ? "geo" : "company"); // company | geo | form | done
  const [companyId, setCompanyId] = useState(publicToken ? "public-link" : "");
  const [geoError, setGeoError] = useState("");
  const [geoLoading, setGeoLoading] = useState(false);
  const [coords, setCoords] = useState(null);

  const [answers, setAnswers] = useState({}); // fieldId -> value
  const [followUps, setFollowUps] = useState({}); // fieldId -> { followUpId: value }
  const [files, setFiles] = useState({}); // "fieldId" | "fieldId:followUpId" -> File
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);

  const setAnswer = (fieldId, value) => setAnswers((prev) => ({ ...prev, [fieldId]: value }));
  const setFollowUp = (fieldId, followUpId, value) => setFollowUps((prev) => ({
    ...prev, [fieldId]: { ...(prev[fieldId] || {}), [followUpId]: value },
  }));
  const setFieldFile = (fieldId, file) => setFiles((prev) => ({ ...prev, [fieldId]: file }));
  const setFollowUpFile = (fieldId, followUpId, file) => setFiles((prev) => ({ ...prev, [`${fieldId}:${followUpId}`]: file }));

  const requestLocation = () => {
    setGeoError("");
    setGeoLoading(true);
    if (!navigator.geolocation) {
      setGeoLoading(false);
      setGeoError("Este dispositivo/navegador no soporta geolocalizacion.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCoords({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
        setGeoLoading(false);
        setStep("form");
      },
      () => {
        setGeoLoading(false);
        setGeoError("No se pudo obtener tu ubicacion. Activa el permiso de ubicacion para continuar.");
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  useEffect(() => {
    if (step === "geo") requestLocation();
  }, [step]);

  const requiredMissing = (inspection.fields || []).some((field) => {
    if (field.type === "compliance") return !answers[field.id];
    if (!field.required) return false;
    if (field.type === "file") return !files[field.id];
    const value = answers[field.id];
    return value === undefined || value === null || value === "";
  });

  const hasFutureDate = (inspection.fields || []).some((field) =>
    field.type === "date" && field.dateCalculation && isFutureDate(answers[field.id])
  );

  const canSubmit = !requiredMissing && !hasFutureDate && submitting !== true;

  const handleSubmit = async () => {
    if (!canSubmit || !coords) return;
    setSubmitting(true);
    setSubmitError("");

    try {
      const payload = {};
      inspection.fields.forEach((field) => {
        if (field.type === "compliance") {
          payload[field.id] = { value: answers[field.id], followUps: followUps[field.id] || {} };
        } else if (field.type !== "file") {
          payload[field.id] = { value: answers[field.id] };
        }
      });

      const formData = new FormData();
      formData.append("company_id", companyId);
      formData.append("latitude", coords.latitude);
      formData.append("longitude", coords.longitude);
      formData.append("accuracy", coords.accuracy || "");
      formData.append("answers", JSON.stringify(payload));

      Object.entries(files).forEach(([key, file]) => {
        if (!file) return;
        if (key.includes(":")) {
          const [fieldId, followUpId] = key.split(":");
          formData.append(`field_${fieldId}_${followUpId}`, file);
        } else {
          formData.append(`field_${key}`, file);
        }
      });

      const response = publicToken
        ? await api.submitPublicInspection(publicToken, formData)
        : await api.submitInspectionRecord(inspection.id, formData);
      if (response.error) throw new Error(response.error);
      setResult(response);
      setStep("done");
      onSubmitted && onSubmitted();
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <motion.div className="modal-overlay"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <motion.div className="modal modal-large"
        initial={{ opacity: 0, scale: 0.92, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.94, y: 12 }}
        transition={{ type: "spring", stiffness: 380, damping: 30, mass: 0.7 }}>

        <div className="modal-header">
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: "#FEF3C7", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <ClipboardCheck size={18} style={{ color: "#B45309" }} />
            </div>
            <h2>{inspection.name}</h2>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="modal-form" style={{ maxHeight: "65vh", overflowY: "auto" }}>
          {step === "company" && (
            <div className="form-row">
              <label>Empresa a inspeccionar *</label>
              <select className="form-input" value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
                <option value="" disabled>Selecciona una empresa</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              {companies.length === 0 && (
                <p style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginTop: 8 }}>
                  Esta inspeccion no esta asignada a ninguna empresa todavia.
                </p>
              )}
            </div>
          )}

          {step === "geo" && (
            <div style={{ textAlign: "center", padding: "30px 0" }}>
              {geoLoading ? (
                <>
                  <Loader size={32} className="spin" opacity={0.5} />
                  <p style={{ marginTop: 14, fontSize: "0.86rem", color: "var(--text-muted)" }}>Obteniendo tu ubicacion actual...</p>
                </>
              ) : geoError ? (
                <>
                  <AlertCircle size={36} style={{ color: "#C53030" }} />
                  <p style={{ marginTop: 14, fontSize: "0.86rem", color: "#C53030" }}>{geoError}</p>
                  <button className="btn-primary" onClick={requestLocation} style={{ marginTop: 14 }}>Reintentar</button>
                </>
              ) : (
                <MapPin size={32} opacity={0.4} />
              )}
            </div>
          )}

          {step === "form" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.76rem", color: "var(--primary)" }}>
                <MapPin size={13} /> Ubicacion capturada ({coords.latitude.toFixed(5)}, {coords.longitude.toFixed(5)})
              </div>
              {inspection.fields.map((field) => (
                field.type === "compliance" ? (
                  <ComplianceQuestion
                    key={field.id}
                    field={field}
                    value={answers[field.id]}
                    onChange={(value) => setAnswer(field.id, value)}
                    followUpValues={followUps[field.id] || {}}
                    onFollowUpChange={setFollowUp}
                    onFollowUpFile={setFollowUpFile}
                  />
                ) : (
                  <GenericQuestion
                    key={field.id}
                    field={field}
                    value={answers[field.id]}
                    onChange={(value) => setAnswer(field.id, value)}
                    onFile={(file) => setFieldFile(field.id, file)}
                  />
                )
              ))}
              {submitError && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", background: "#FEE2E2", borderRadius: 8, color: "#C53030", fontSize: "0.78rem" }}>
                  <AlertCircle size={15} /> {submitError}
                </div>
              )}
            </div>
          )}

          {step === "done" && result && (
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              <CheckCircle size={44} className="success-icon" />
              <h3 style={{ marginTop: 14 }}>Inspeccion guardada</h3>
              <p style={{ marginTop: 8, fontSize: "0.9rem" }}>
                Cumplimiento: <b>{result.score_percent}%</b> · Criticidad: <b>{result.criticality}</b>
              </p>
              <p style={{ marginTop: 6, fontSize: "0.78rem", color: "var(--text-muted)" }}>
                Se genero el documento en la carpeta "Inspecciones" de la empresa y se notifico de inmediato.
              </p>
            </div>
          )}
        </div>

        <div className="modal-footer">
          {step === "done" ? (
            <button className="btn-primary" onClick={onClose} style={{ marginLeft: "auto" }}>Cerrar</button>
          ) : (
            <>
              <button className="btn-secondary" onClick={onClose}>Cancelar</button>
              {step === "company" && (
                <button className="btn-primary" onClick={() => setStep("geo")} disabled={!companyId}>Continuar</button>
              )}
              {step === "form" && (
                <button className="btn-primary" onClick={handleSubmit} disabled={!canSubmit}>
                  {submitting ? <><Loader size={14} className="spin" /> Guardando...</> : "Guardar inspeccion"}
                </button>
              )}
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
